import { ForwardRefRenderFunction, forwardRef, useEffect, useMemo, useRef } from "react";
import { Grid } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";

// #region types
import { CurrencyEnums, WintSalaryEnums } from "models/enums";
import { DisplayType } from "models/enums/style";
import { SalaryFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { SimpleTableRow } from "models/propTypes/simpleTableProps";
import { CurrencyInputProps, DateInputProps, SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { EarlyPayoutFormProps } from "models/propTypes/wintSalaryPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
// #endregion

// #region components
import { ChoiceChips, Icon, SimpleAccordionTable, StaticInfoMessage } from "components/common";
import {
  CurrencyInput,
  DateInput,
  Description,
  Label,
  SearchInput,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import useDeviationPermissions from "components/salaryDeviation/usePermissions";
import useWintSalaryService from "serviceHooks/useWintSalaryService";
import { TranslationKeys } from "translations";
import { FieldConstants, IconDefinitionConstants, SalaryConstants } from "utils/constants";
import { CurrencyHelper, DateHelper, LocalizationHelperInstance } from "utils/helpers";
// #endregion

const EarlyPayoutForm: ForwardRefRenderFunction<FormHandles, EarlyPayoutFormProps> = (props, ref) => {
  const {
    defaultValues,
    validationSchema,
    handleSubmit,
    onFormValidationChange,
    preview,
    onEmployeeChange,
    onPayoutMonthChange,
    validPayoutDays,
    earlyPayoutInfo,
    onPreview,
    loading,
    previewLoading,
  } = props;

  const salaryPayDate = earlyPayoutInfo?.payoutInfo?.salaryPayDate
    ? new Date(earlyPayoutInfo.payoutInfo.salaryPayDate)
    : undefined;

  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();

  const { wintFormProps, connect, values } = useWintForm<SalaryFormTypes.EarlyPayout>({
    formRef: ref,
    defaultValues: defaultValues,
    handleSubmit: handleSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onFormValidationChange: onFormValidationChange,
  });

  const employeeConnector = connect("employee");
  const payoutDateConnector = connect("payoutDate");
  const amountTypeConnector = connect("amountType");
  const payoutAmountConnector = connect("payoutAmount");
  const payoutTypeConnector = connect("payoutType");
  const useOneTimeTaxConnector = connect("useOneTimeTax");

  const previousEmployeeId = useRef<string | number | undefined>(undefined);

  const searchUsers = (searchStr: string) =>
    searchPersonsAutoComplete(
      searchStr,
      false,
      true,
      isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
    );

  useEffect(() => {
    const currentId = values.employee?.id;
    if (currentId && currentId !== previousEmployeeId.current) {
      // Preserve the user's typed values across employee switches. Clearing payoutAmount while
      // the input is focused/touched fires the required-field validation ("Var vänlig fyll i det
      // här fältet") — abrupt and confusing. The payoutDate is kept while it is still valid
      // and the info follows its month; exceedsAvailable/auto-switch re-evaluates payoutType
      // for the new context; the amount input is disabled during load so nothing stale submits.
      onEmployeeChange?.(Number(currentId));
    }
    previousEmployeeId.current = currentId;
  }, [values.employee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!validPayoutDays?.length) return;
    // Reseating a date the user chose would send the form back to the earliest valid day's month
    // every time the info for the chosen month arrives.
    const payoutDate = values.payoutDate;
    const chosenDateIsValid = !!payoutDate && validPayoutDays.some((day) => DateHelper.isEqual(day, payoutDate));
    if (!chosenDateIsValid) {
      payoutDateConnector.setValue(validPayoutDays[0]);
    }
  }, [earlyPayoutInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const employeeId = values.employee?.id;
    // An API that predates yearMonth leaves it unset, which must not read as a mismatch: the
    // refetch would return the same unset month and loop.
    if (!employeeId || !DateHelper.isValidDate(values.payoutDate) || !earlyPayoutInfo?.yearMonth || loading) return;
    const payoutMonth = DateHelper.getYearMonth(values.payoutDate!);
    if (payoutMonth !== earlyPayoutInfo.yearMonth) {
      onPayoutMonthChange?.(Number(employeeId), payoutMonth);
    }
  }, [values.payoutDate, earlyPayoutInfo, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedPreview = useDebouncedCallback((formValues: SalaryFormTypes.EarlyPayout) => {
    onPreview?.(formValues);
  }, FieldConstants.DebounceTime);

  useEffect(() => {
    if (
      !values.employee ||
      !DateHelper.isValidDate(values.payoutDate) ||
      !values.payoutAmount ||
      values.payoutAmount < 1
    )
      return;
    if (values.amountType === undefined || values.payoutType === undefined) return;
    debouncedPreview(values);
  }, [values.payoutDate, values.payoutAmount, values.amountType, values.payoutType, values.useOneTimeTax]); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeSelected = !!values.employee;

  // Amount type chip items
  const amountTypeItems: SelectionItem[] = useMemo(
    () => [
      {
        id: WintSalaryEnums.EarlyPayoutAmountType.Net,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutAmountTypeNet),
      },
      {
        id: WintSalaryEnums.EarlyPayoutAmountType.Gross,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutAmountTypeGross),
      },
    ],
    []
  );

  // Payout type chip items
  const payoutTypeItems: SelectionItem[] = useMemo(
    () => [
      {
        id: WintSalaryEnums.EarlyPayoutKind.ExtraSalary,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPayoutTypeExtra),
      },
      {
        id: WintSalaryEnums.EarlyPayoutKind.SalaryAdvance,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPayoutTypeAdvance),
      },
    ],
    []
  );

  // Tax type chip items (reuse existing translations from gross salary change)
  const taxTypeItems: SelectionItem[] = useMemo(
    () => [
      {
        id: WintSalaryEnums.ChangeGrossSalaryTaxType.TableTax,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ChangeGrossSalaryTaxType_0),
      },
      {
        id: WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax,
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ChangeGrossSalaryTaxType_1),
      },
    ],
    []
  );

  // Determine if amount exceeds the original available salary (before any gross adjustment).
  // payoutAmount can be gross when amountType=GrossSalary, so we have to compare net-to-net:
  // use the backend-computed net-equivalent from preview when available, fall back to undefined
  // (skip the check) while the first preview is still loading. Also skipped while the info for a
  // newly chosen month is loading, and when that month has no info at all (e.g. no salary yet):
  // judging the amount against the previous month or against a fallback 0 would auto-switch to
  // Extra lön, and that switch is one-way, so it would outlive a return to a valid date.
  const originalRemainingNet = earlyPayoutInfo?.payoutInfo?.remainingNet ?? 0;
  const effectiveNetAmount =
    values.amountType === WintSalaryEnums.EarlyPayoutAmountType.Gross
      ? preview?.calculatedNetAmount
      : values.payoutAmount;
  const exceedsAvailable =
    !loading &&
    earlyPayoutInfo?.payoutInfo !== undefined &&
    effectiveNetAmount !== undefined &&
    effectiveNetAmount > originalRemainingNet &&
    originalRemainingNet >= 0 &&
    employeeSelected;

  // Show tax type selector when the previewed gross-salary increase (totalGrossAfterAdjustment -
  // monthlyGrossSalary) crosses the threshold — same delta semantics as "Ändra månadens bruttolön".
  // Comparing the raw payoutAmount would miss net entries that translate to a gross delta past the
  // threshold (a 35k net payout for a high earner can be ~60k gross).
  const grossSalaryIncreaseFromPreview =
    preview?.grossSalaryPreview?.totalGrossAfterAdjustment !== undefined &&
    preview?.grossSalaryPreview?.monthlyGrossSalary !== undefined
      ? preview.grossSalaryPreview.totalGrossAfterAdjustment - preview.grossSalaryPreview.monthlyGrossSalary
      : undefined;
  const showTaxTypeSelector =
    exceedsAvailable &&
    grossSalaryIncreaseFromPreview !== undefined &&
    grossSalaryIncreaseFromPreview >= SalaryConstants.GrossSalaryChangeTaxTypeSelectionThreshold;

  useEffect(() => {
    if (exceedsAvailable && values.payoutType !== WintSalaryEnums.EarlyPayoutKind.ExtraSalary) {
      payoutTypeConnector.setValue(WintSalaryEnums.EarlyPayoutKind.ExtraSalary);
    }
  }, [exceedsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset useOneTimeTax when the tax type selector is hidden so a stale "on" doesn't leak into
  // the preview/submit payload after the user lowers the amount below the threshold or switches
  // to an employee where the selector no longer applies.
  useEffect(() => {
    if (!showTaxTypeSelector && values.useOneTimeTax) {
      useOneTimeTaxConnector.setValue(false);
    }
  }, [showTaxTypeSelector]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dynamic amount label based on amountType
  const amountLabel =
    values.amountType === WintSalaryEnums.EarlyPayoutAmountType.Gross
      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutGrossSalaryToBePaid)
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutNetSalaryToBePaid);

  // --- Utbetalningar section data ---
  const displaySalaryPayDate = preview?.payoutInfoPreview?.salaryPayDate
    ? new Date(preview.payoutInfoPreview.salaryPayDate)
    : salaryPayDate;
  // paymentDay must be numeric — the Swedish/English translation for EarlyPayoutRemainingNetRow
  // uses `{paymentDay, selectordinal, ...}` which expects a number, not a string.
  const paymentDay = displaySalaryPayDate ? displaySalaryPayDate.getDate() : undefined;
  const paymentMonth = displaySalaryPayDate
    ? LocalizationHelperInstance.getFormattedShortMonth(displaySalaryPayDate)
    : "";

  const remainingNet = preview
    ? (preview.payoutInfoPreview?.remainingNet ?? 0)
    : (earlyPayoutInfo?.payoutInfo?.remainingNet ?? 0);

  const showWarning = preview !== undefined && !preview.hasSpace;

  const earlyPayoutSummary: SimpleTableRow[] = [];

  // Row 1: Proposed payout (always shown in net)
  const proposedNetAmount =
    preview?.calculatedNetAmount ??
    (values.amountType === WintSalaryEnums.EarlyPayoutAmountType.Net ? values.payoutAmount : undefined);

  if (DateHelper.isValidDate(values.payoutDate) && proposedNetAmount !== undefined) {
    const proposedDay = String(new Date(values.payoutDate!).getDate());
    const proposedMonth = LocalizationHelperInstance.getFormattedShortMonth(new Date(values.payoutDate!));
    earlyPayoutSummary.push({
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutProposedPayoutRow, "", {
        payoutDay: proposedDay,
        payoutMonth: proposedMonth,
      }),
      value: CurrencyHelper.getFormattedCurrencyInSEK(proposedNetAmount),
    });
  }

  // Row 2: Remaining net salary (paid on regular payday)
  if (earlyPayoutInfo?.payoutInfo || preview?.payoutInfoPreview) {
    earlyPayoutSummary.push({
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutRemainingNetRow, "", {
        paymentDay,
        paymentMonth,
      }),
      value: showWarning ? (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
          <Icon icon={IconDefinitionConstants.WExclamation} displayType={DisplayType.Warning} />
          {CurrencyHelper.getFormattedCurrencyInSEK(remainingNet)}
        </span>
      ) : (
        CurrencyHelper.getFormattedCurrencyInSEK(remainingNet)
      ),
    });
  }

  // --- Result section data (always shown when preview available, with expandable sub-rows) ---
  const grossSalarySubContent: SimpleTableRow[] = [];
  if (preview?.grossSalaryPreview) {
    const gp = preview.grossSalaryPreview;
    grossSalarySubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewGrossSalaryMonthlySalary
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(gp.monthlyGrossSalary ?? 0),
    });
    grossSalarySubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewGrossSalaryDeviation
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(gp.deviations ?? 0),
    });
    grossSalarySubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewGrossSalarySalaryAdjustments
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(gp.salaryAdjustments ?? 0),
    });
    grossSalarySubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewGrossSalaryBenefits
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(gp.benefits ?? 0),
    });
  }

  const grossSalarySummary: SimpleTableRow = {
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPreviewGrossSalaryTitle),
    value: CurrencyHelper.getFormattedCurrencyInSEK(preview?.grossSalaryPreview?.totalGrossAfterAdjustment ?? 0),
    emphasisValue: true,
    subContent: grossSalarySubContent.length > 0 ? grossSalarySubContent : undefined,
  };

  const companyCostSubContent: SimpleTableRow[] = [];
  if (preview?.companyPreview) {
    const cp = preview.companyPreview;
    companyCostSubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewCompanyCostRemainingNetSalary
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(cp.remainingNetSalaries ?? 0),
    });
    companyCostSubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewCompanyCostAllExtraSalaries
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(cp.totalEarlyPayouts ?? 0),
    });
    companyCostSubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewCompanyCostTaxCost
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(cp.taxes ?? 0),
    });
    companyCostSubContent.push({
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Salary.ExtraPayoutPreviewCompanyCostSocialFees
      ),
      value: CurrencyHelper.getFormattedCurrencyInSEK(cp.socialFees ?? 0),
    });
  }

  const totalCostSummary: SimpleTableRow = {
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPreviewCompanyCostTitle),
    value: CurrencyHelper.getFormattedCurrencyInSEK(preview?.companyPreview?.totalCompanyCost ?? 0),
    emphasisValue: true,
    subContent: companyCostSubContent.length > 0 ? companyCostSubContent : undefined,
  };

  return (
    <WintForm {...wintFormProps}>
      <Grid container direction="column" rowGap="24px" paddingY="12px">
        {/* Employee + Date */}
        <Grid container direction="column" rowGap="16px">
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...employeeConnector}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Employee)}
            items={searchUsers}
            icon={IconDefinitionConstants.WUser}
            noOuterSpacing
          />
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...payoutDateConnector}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PayoutDate)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Salary.PaymentDateDescription
            )}
            disabled={!employeeSelected || loading}
            minDate={validPayoutDays?.[0]}
            maxDate={validPayoutDays?.[validPayoutDays.length - 1]}
            validDates={validPayoutDays}
            noOuterSpacing
          />
        </Grid>
        {/* Amount type */}
        <Grid container direction="column" rowGap="16px">
          <Grid container direction="column">
            <Label>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutAmountTypeLabel)}
            </Label>
            <ChoiceChips
              title=""
              items={amountTypeItems}
              value={employeeSelected ? amountTypeItems.find((i) => i.id === values.amountType) : undefined}
              onChange={(item) => amountTypeConnector.setValue(item.id)}
              disabled={!employeeSelected || loading}
            />
          </Grid>
          <VerticalFormField<CurrencyInputProps>
            Component={CurrencyInput}
            {...payoutAmountConnector}
            currency={CurrencyEnums.CurrencyCode.SEK}
            label={amountLabel}
            disabled={!employeeSelected || loading}
            isLoading={previewLoading}
            noOuterSpacing
          />
        </Grid>
        {/* Payout type + conditional info + tax type */}
        <Grid container direction="column" rowGap="16px">
          <Grid container direction="column">
            <Label>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPayoutTypeLabel)}
            </Label>
            <ChoiceChips
              title=""
              items={payoutTypeItems}
              value={employeeSelected ? payoutTypeItems.find((i) => i.id === values.payoutType) : undefined}
              onChange={(item) => payoutTypeConnector.setValue(item.id)}
              disabled={!employeeSelected || loading}
              disabledItems={exceedsAvailable ? [WintSalaryEnums.EarlyPayoutKind.SalaryAdvance] : []}
            />
          </Grid>
          {exceedsAvailable && (
            <StaticInfoMessage>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutExceedsAvailableInfo)}
            </StaticInfoMessage>
          )}
        </Grid>
        {/* Tax type section: shown when amount >= 50k threshold (same as "Ändra månadens bruttolön") */}
        {showTaxTypeSelector && (
          <Grid container direction="column">
            <Label>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutTaxTypeLabel)}
            </Label>
            <ChoiceChips
              title=""
              items={taxTypeItems}
              value={taxTypeItems.find(
                (i) =>
                  i.id ===
                  (values.useOneTimeTax
                    ? WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax
                    : WintSalaryEnums.ChangeGrossSalaryTaxType.TableTax)
              )}
              onChange={(item) =>
                useOneTimeTaxConnector.setValue(item.id === WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax)
              }
            />
            <Description>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Salary.ChangeGrossSalaryTaxTypeDescription
              )}
            </Description>
          </Grid>
        )}
        {/* Utbetalningar + Resultat */}
        {employeeSelected && (
          <Grid container direction="column" rowGap="16px">
            <Grid container direction="column">
              <Label>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPaymentsLabel)}
              </Label>
              {earlyPayoutSummary.length > 0 && (
                <SimpleAccordionTable variant="secondary" emphasisValue items={earlyPayoutSummary} />
              )}
            </Grid>
            {preview && (
              <Grid container direction="column">
                <Label>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutResultLabel)}
                </Label>
                <SimpleAccordionTable variant="secondary" items={[grossSalarySummary, totalCostSummary]} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </WintForm>
  );
};

export default forwardRef(EarlyPayoutForm);
